/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { Users, RefreshCw, BookOpen, HelpCircle, Eye, GraduationCap, ChevronDown, ChevronUp, Clock } from "lucide-react";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { Training, getStatusStyles, formatTime } from "../sharedUtils";
import { FaRegTrashAlt } from "react-icons/fa";

// --- TRAINING CARD ---
export function TrainingCard({ training, generatingId, onGenerate, onAssign, onPreview, onDelete }: { training: Training, generatingId: number | null, onGenerate: (id: number) => void, onAssign: (id: number) => void, onPreview: (id: number) => void, onDelete: (id: number) => void }) {
    const isGenerating = training.trainingStatus === 'GENERATING' || training.trainingStatus === 'PROCESSING';
    return (
        <div className="p-4 bg-white dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-xl flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow relative min-w-0">
            <div className="pr-8 min-w-0">
                <div className="flex justify-between items-start mb-1 gap-2 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate" title={training.trainingName}>{training.trainingName}</h3>
                    <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-md shrink-0 ${getStatusStyles(training.trainingStatus)}`}>{training.trainingStatus}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1" title={training.domainName}>Domain: <span className="font-medium text-gray-700 dark:text-gray-300">{training.domainName}</span></p>

                {/* ADDED: Uploaded Time using formatTime alongside Lessons and Qs */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2.5 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1"><BookOpen size={13} /> {training.lessonCount} Lessons</div>
                    <div className="flex items-center gap-1"><HelpCircle size={13} /> {training.exerciseCount} Qs</div>
                    <div className="flex items-center gap-1" title="Uploaded On"><Clock size={13} /> {formatTime(training.uploadedAt)}</div>
                </div>
            </div>

            {/* Delete Action (Top Right) */}
            <button
                onClick={() => onDelete(training.trainingId)}
                className="group absolute top-3 right-3 p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shadow-sm cursor-pointer"
                title="Delete Training"
            >
                <FaRegTrashAlt size={16} className="text-error group-hover:scale-110 transition-transform" />
            </button>

            {/* ACTION BUTTONS */}
            <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700/50 flex flex-wrap gap-2">
                <button onClick={() => onGenerate(training.trainingId)} disabled={training.trainingStatus !== 'PENDING' || generatingId === training.trainingId} className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors text-gray-700 dark:text-gray-300 w-fit">
                    {isGenerating ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />} Generate
                </button>
                <button onClick={() => onAssign(training.trainingId)} disabled={training.trainingStatus !== 'READY'} className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 transition-colors w-fit">
                    <Users size={12} /> Assign
                </button>
                <button onClick={() => onPreview(training.trainingId)} disabled={training.trainingStatus !== 'READY'} className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border transition-colors bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 w-fit">
                    <Eye size={12} /> Preview
                </button>
            </div>
        </div>
    );
}

// --- EMPLOYEE PROGRESS COMPONENT ---
export function EmployeeProgress() {
    const { addToast } = useToaster();
    const [trainings, setTrainings] = useState<Training[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [assignmentData, setAssignmentData] = useState<any[]>([]);
    const [loadingAssignments, setLoadingAssignments] = useState(false);

    useEffect(() => {
        const fetchTrainings = async () => {
            setLoading(true);
            try {
                const res = await apiService.get("api/gp/trainings");
                setTrainings(res.data || []);
            } catch (error: any) {
                addToast(error?.response?.data?.error || "Failed to load trainings.", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchTrainings();
    }, [addToast]);

    const toggleExpand = async (trainingId: number) => {
        if (expandedId === trainingId) { setExpandedId(null); return; }
        setExpandedId(trainingId);
        setLoadingAssignments(true);
        setAssignmentData([]);
        try {
            const res = await apiService.get(`api/gp/progress/training/${trainingId}`);
            setAssignmentData(res.data || []);
        } catch (error: any) {
            addToast(error?.response?.data?.error || "Failed to load assignments.", "error");
        } finally {
            setLoadingAssignments(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            <div className="flex items-center gap-2 mb-6">
                <GraduationCap className="text-blue-600 dark:text-blue-400" size={24} />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Employee Progress Directory</h3>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="animate-spin text-gray-400" size={24} /></div>
            ) : trainings.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">No trainings available in the system yet.</div>
            ) : (
                <div className="grid gap-5">
                    {trainings.map((t) => (
                        <div key={t.trainingId} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all duration-200">
                            <div onClick={() => toggleExpand(t.trainingId)} className="px-6 py-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 flex flex-wrap justify-between items-center gap-4 transition-colors">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-1 min-w-0">
                                        <h4 className="text-lg font-bold text-gray-900 dark:text-white truncate" title={t.trainingName}>{t.trainingName}</h4>
                                        <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-md shrink-0 ${getStatusStyles(t.trainingStatus)}`}>{t.trainingStatus}</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium truncate" title={t.domainName}>{t.domainName}</p>
                                </div>
                                <div className="flex items-center gap-6 shrink-0">
                                    <div className="hidden sm:block text-right">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Uploaded</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-200">{formatTime(t.uploadedAt)}</p>
                                    </div>
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-500 dark:text-gray-300">
                                        {expandedId === t.trainingId ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                    </div>
                                </div>
                            </div>

                            {expandedId === t.trainingId && (
                                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                                    {loadingAssignments ? (
                                        <div className="flex items-center justify-center py-8"><RefreshCw className="animate-spin text-gray-400" size={20} /></div>
                                    ) : assignmentData.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">No employees have been assigned to this training yet.</div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left">
                                                <thead className="text-xs text-gray-500 uppercase bg-gray-100 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700">
                                                    <tr>
                                                        <th className="px-6 py-3">Employee Name</th>
                                                        <th className="px-6 py-3">Status</th>
                                                        <th className="px-6 py-3">Progress</th>
                                                        <th className="px-6 py-3">Score</th>
                                                        <th className="px-6 py-3">Assigned Date</th>
                                                        <th className="px-6 py-3">Started / Finished</th>
                                                        </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                                    {assignmentData.map((user: any, idx: number) => (
                                                        <tr key={idx} className="hover:bg-white dark:hover:bg-gray-700/30 transition-colors">
                                                            <td className="px-6 py-4"><p className="font-semibold text-gray-900 dark:text-white">{user.userName}</p><p className="text-xs text-gray-500 dark:text-gray-400">{user.userEmail}</p></td>
                                                            <td className="px-6 py-4"><span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-md ${user.status === 'COMPLETED' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : user.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>{user.status?.replace('_', ' ')}</span></td>
                                                            <td className="px-6 py-4 text-gray-600 dark:text-gray-300 font-medium">{user.attempted} / {user.totalExercises}</td>
                                                            <td className="px-6 py-4">{user.attempted > 0 ? <span className="font-bold text-indigo-600 dark:text-indigo-400">{Math.round(user.averageScore * 100)}%</span> : '-'}</td>
                                                            <td className="px-6 py-4 text-gray-500 text-xs">{formatTime(user.assignedAt)}</td>
                                                            <td className="px-6 py-4 text-xs text-gray-500"><div className="space-y-1">{user.startedAt ? <p><span className="font-medium">Started:</span> {formatTime(user.startedAt)}</p> : <p className="text-gray-400">Not started</p>}{user.finishedAt && <p><span className="font-medium text-green-600 dark:text-green-400">Finished:</span> {formatTime(user.finishedAt)}</p>}</div></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}